import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DataGrid,
  GridColDef,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridFooterContainer,
  GridFooter,
} from "@mui/x-data-grid";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import { Alert, Snackbar, Tooltip, Button, Box, CircularProgress, Checkbox, Select, MenuItem } from "@mui/material";
import { logAnalyticsEvent, analytics, performance } from "../../../src/firebase";
import { logEvent } from "firebase/analytics";
import { ReactComponent as DisclaimerIcon } from "../../assets/image/svg/disclaimer.svg";
import MultiQtyCell from "./MultiQtyCell";
import ExpiryDateCell from "./ExpiryDateCell";
import { truncateText } from "../../features/stocks/RealTimeStock/RealTimeStockUtils";
import { formatDecimal } from "../../components/dms_common/DmsUtils";
import RenderTooltipContent from "./RenderTooltipContent";
import CallMadeIcon from "@material-ui/icons/CallMade";
import InfoIcon from "@mui/icons-material/Info"; 
import ErrorIcon from '@mui/icons-material/Error';
import "./CustomDataGrid.css";
import ProgressUrl from "../../assets/gif/progress.gif";
import "./MultiQtyCell.css";
import RenderSvg from "../renderSvg/RenderSvg";
import { useNavigate } from "react-router-dom";
import { getCasePieceSumProps } from "../../features/stocks/getQuantityProps";
import {
  orderStatusConfig,
  popupType,
  grnStatusConfig,
  DownloadSingleReportConfig,
  orderSourceConfig,
} from "../../types/types";
import { GenericPopUp } from "../popup/genericPopUp";
import OrderStatusCell from "./OrderStatusCell";
import StockTypeTransferCell from "./StockTypeTransferCell";
import {
  setDiscountModalState,
  setOpen,
  setSelectedRowToEdit,
  setSelectedSecondarySchemesRow,
  setSelectedInvoiceRow
} from "../../stateManagement/actions/claims";
import { ReactComponent as DeleteIcon } from "../../assets/image/svg/deleteIcon.svg";
import { ReactComponent as CircledRight } from "../../assets/image/svg/circledRight.svg";
import { ReactComponent as EditPen } from "../../assets/image/svg/editPen.svg";
import dayjs from "dayjs";
import ShelfLifeCustomFooter from "./ShelfLifeCustomFooter";
import { downloadSingleReport } from "../../services/manageReportService";
import { ReactComponent as DownloadIcon } from "../../assets/image/svg/downloadIcon.svg";
import store from "../../stateManagement/store";
import { ReactComponent as PdfIcon } from "../../assets/image/svg/pdfIcon.svg";
import { GstFlagging } from "../../features/secondaryOrder/ManageSecondaryInvoice";
import { CancelledInvoiceFlagging } from "../../features/secondaryOrder/ManageSecondaryOrderV2";
import { customStyle } from "../../features/viewOrder/ViewOrderUtils";
import { BulkType, renderBulkStatus, RenderBulkStatusProps } from "../../features/secondaryOrder/BulkUtils";
import { getConfigData } from "../../services/configurationService";
import MultiQtyInputCell from "./MultiQtyInputCell";
import useShortcutKeys from "./ShortcutKeys";
import { BulkConfirmConfigType, defaultBulkConfirmLabels } from "../../features/secondaryOrder/BulkConfirm";
import { Margin } from "@mui/icons-material";
import OutletWiseAllocationModal from "../modals/OutletWiseAllocationModal";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ReplacementPopoverCell } from "../../features/secondaryOrder/ReplacementPopOverCell";
import OrderTypeCell from "./OrderTypeCell";
import UploadStatusCell from "./UploadStatusCell";


interface CustomDataGridProps {
  rows: any[];
  columnsMetaData: { columns: any[]; Actions: string[] };
  orderStatusConfig?: orderStatusConfig[];
  grnStatusConfig?: grnStatusConfig[];
  redirectUrl?: string;
  viewIcon?: string;
  openButtonNavigateUrl?: string;
  isCheckboxSelection?: boolean;
  height?: string;
  insidePurchaseReturns?: boolean;
  handleClickEditPurchaseReturnsRow?: Function;
  insideSecondarySchemes?: boolean;
  insideSaleReturns?: boolean;
  onSalesReturnLandingScreen?: boolean;
  tableWidth?: number;
  horizontalScrollBehaviour?: boolean;
  showShelfLifeFooter?: boolean;
  handleDeleteRow?: Function;
  checkedIdArray?: any[];
  setCheckedIdArray?: (ids: any[]) => void;
  downloadSingleReportConfig?: DownloadSingleReportConfig | null;
  redirectUrlFunction?: Function;
  searchedValue?:string;
  handleClickBulk?: () => void;
  handleBulkNav?: () => void;
  isAnyPendingOrder?: boolean;
  bulkType?: BulkType;
  footerButtonLoading?: boolean;
  noDataMessage?: string;
  disabledRowIds?: any [];
  rowIdsWithCheckboxHidden?: any [];
  printBulkInvoices?: () => void;
  printInvoicesLoading?: boolean;
  locationState?: Record <any, any>;
  onDownloadGeneric?: Function;
  orderSourceConfig?: Record<string, orderSourceConfig>;
  footerElement?: () => JSX.Element;
  selectableRowIds?: any[];
  onSave?: Function;
  onDeleteFunction?: Function;
  isPrimaryOrder?: boolean;
  onRowClick?: (params: any) => void;
  selectedRowId?: any;
  insideOpenClaimsDetails?: boolean;
  onEditClick?: (row: any) => void;
  handleClickCreditNote?: (creditNoteNumber: string) => void;
  insideOutletApproval?:boolean;
  disableOrderEditDesignationBasedChild?:boolean;
  enableRowClickRedirect?: boolean;
  setScrollTop?: React.Dispatch<React.SetStateAction<number>>;
  isScrolling?: boolean
  allowTableScroll?: boolean;
  orderConfirmationEnabled?:boolean;
  isPrimaryModuleReport?: boolean;
  fadedRowIds?: string[];
  setSortByColumn?: React.Dispatch<React.SetStateAction<any>>;
  numberOfSelectedInvoicedOrders?:number;
  insideManageOrder?:boolean;
  bulkInvoiceButtonLoading?:boolean;
  handleStockAllocation?: () => void;
  insideManualStockAllocation?: boolean;
  isFooterVisible?:boolean;
  handleBulkInvoiceModalClick?: () => void;
  insideBulkInvoiceModal?: boolean;
  handleBulkInvoicingForManualStockAllocation?: () => void;
  stockAllocationAllowed?: boolean;
  isAnyManualAndPending?: boolean;
  isStockAllocationButtonVisible?: boolean;
  isBulkOrderConfirmAllowedFromBC?:boolean
}

const CustomDataGrid: React.FC<CustomDataGridProps> = ({
  rows = [],
  columnsMetaData,
  orderStatusConfig,
  redirectUrl,
  viewIcon,
  openButtonNavigateUrl,
  isCheckboxSelection,
  insidePurchaseReturns,
  handleClickEditPurchaseReturnsRow,
  insideSecondarySchemes,
  insideSaleReturns,
  onSalesReturnLandingScreen,
  tableWidth,
  horizontalScrollBehaviour = false,
  showShelfLifeFooter,
  handleDeleteRow,
  checkedIdArray,
  setCheckedIdArray,
  grnStatusConfig,
  downloadSingleReportConfig,
  redirectUrlFunction,
  searchedValue,
  handleClickBulk,
  handleBulkNav,
  isAnyPendingOrder,
  bulkType,
  footerButtonLoading,
  noDataMessage,
  disabledRowIds = [],
  rowIdsWithCheckboxHidden = [],
  printBulkInvoices,
  printInvoicesLoading = false,
  locationState = {},
  onDownloadGeneric,
  orderSourceConfig = {},
  footerElement,
  selectableRowIds,
  onSave,
  onDeleteFunction,
  onEditClick, 
  handleClickCreditNote,
  isPrimaryOrder,
  onRowClick,
  fadedRowIds,
  selectedRowId,
  insideOpenClaimsDetails = false,
  insideOutletApproval,
  disableOrderEditDesignationBasedChild = false,
  enableRowClickRedirect = true,
  setScrollTop = () => {},
  isScrolling,
  allowTableScroll = true,
  orderConfirmationEnabled = true,
  isPrimaryModuleReport = false,
  setSortByColumn,
  numberOfSelectedInvoicedOrders,
  insideManageOrder=false,
  bulkInvoiceButtonLoading=false,
  handleStockAllocation,
  insideManualStockAllocation=false,
  isFooterVisible=true,
  handleBulkInvoiceModalClick,
  insideBulkInvoiceModal=false,
  handleBulkInvoicingForManualStockAllocation,
  stockAllocationAllowed,
  isAnyManualAndPending,
  isStockAllocationButtonVisible,
  isBulkOrderConfirmAllowedFromBC
}) => {
  
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const reportDownloadSuccess = useRef<boolean>(true)
  const [widthFixedColumns, setWidthFixedColumns] = useState(columnsMetaData);
  const [renderGrid, setRenderGrid] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [isDownload, setIsDownload] = useState<boolean>(false); 
  const [errorReason, setErrorReason] = useState<String>(""); 
  const sidebarCollapsed: boolean = useSelector(
    () => store.getState().sidebarState.sidebarCollapsed
  );
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'none' | 'asc' | 'desc';
    type: string | null;
  }>({ key: null, direction: 'none', type: "number" }); 
  
 const { positiveCta, negativeCta } = useShortcutKeys();
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const manageSecondaryOrderV2 = ConfigDictionary?.manageSecondaryOrderV2 ?? false;
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [sidebarCollapsed]);

  useEffect(() => {
    let maximumTableWidth =
      (tableWidth ?? 0.75) * screenWidth - (isCheckboxSelection ? 50 : 0);

    if (sidebarCollapsed) maximumTableWidth += screenWidth * 0.079861;

    const currentTotalColumnsWidth = columnsMetaData?.columns.reduce(
      (acc, column) => acc + column.width,
      0
    );
    const difference = Math.round(maximumTableWidth - currentTotalColumnsWidth);

    const newColumnsData = columnsMetaData?.columns.map((column) => {
      if (horizontalScrollBehaviour) return column;

      const ratio = column.width / currentTotalColumnsWidth;

      if (difference > 0) {
        const additionalWidth = Math.round(ratio * difference);
        return {
          ...column,
          width: column.width + additionalWidth,
        };
      } else if (difference < 0) {
        const reductionWidth = Math.round(ratio * -difference);
        return {
          ...column,
          width: Math.max(column.width - reductionWidth, 0),
        };
      }
      return column;
    });

    setWidthFixedColumns({ ...columnsMetaData, columns: newColumnsData });
    setRenderGrid(true);
  }, [screenWidth, columnsMetaData, sidebarCollapsed]);

  const RenderBulkStatus: React.FC<RenderBulkStatusProps > = ({ status, type = "invoice" }) => {
      if (status == "Loading") {
        return (
          <div className="BulkInvoice_loading">
            <img src={ProgressUrl} />
          </div>
        );
      } else if (status == "Downloaded Fail") {
        return (
          <div className="BulkInvoice_downloadFailed">
            <DisclaimerIcon />{" "}
            <Tooltip title="Incomplete Stocks" arrow>
              <span>Incomplete Stocks</span>
            </Tooltip>
          </div>
        );
      } else {
        return <></>;
      }
  };

  const renderMultiQtyCell = (columnMetaData: any) => {
    return (
      columnMetaData.includeQty === true ||
      columnMetaData.headerName?.toLowerCase() === "damage qty" ||
      columnMetaData.headerName?.toLowerCase() === "total qty" ||
      columnMetaData.headerName?.toLowerCase() === "approved qty" ||
      columnMetaData.headerName?.toLowerCase() === "loadout qty" ||
      columnMetaData.headerName?.toLowerCase() === "sold qty" ||
      columnMetaData.headerName?.toLowerCase() === "load in qty" ||
      columnMetaData.headerName?.toLowerCase() === "qty" ||
      (columnMetaData.headerName?.toLowerCase() === "quantity" &&
        columnMetaData.type?.toLowerCase() === "order" &&
        columnMetaData.mappedValue.includes(",")) || 
        columnMetaData.qtyDisplay
    );
  };

  useEffect(() => {
    const handleScrollEnd = () => {
      if (setScrollTop && el) {
        const scrollTopValue = el.scrollTop;
        setScrollTop(scrollTopValue);
      }
    }
    
    const el = document.querySelector(".MuiDataGrid-virtualScroller");
    if (el) {
      el.addEventListener("scrollend", handleScrollEnd)
    }
  
    return () => {
      if (el) {
        el.removeEventListener("scrollend", handleScrollEnd);
      }
    };
  }, [setScrollTop, isScrolling]);

  const handleSortChange = (columnKey: string, sortType: string): { key: string | null; direction: 'none' | 'asc' | 'desc' } => {
    let nextSortConfig: typeof sortConfig;
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        if (prev.direction === 'none') {
          nextSortConfig = { key: columnKey, direction: 'asc', type: sortType };
        } else if (prev.direction === 'asc') {
          nextSortConfig = { key: columnKey, direction: 'desc', type: sortType };
        } else {
          nextSortConfig = { key: null, direction: 'none', type: sortType };
        }
      } else {
        nextSortConfig = { key: columnKey, direction: 'asc', type: sortType };
      }
      return nextSortConfig;
    });
    return nextSortConfig!;
  };

  const EditableFinalAmountCell = ({ value, onChange }: { value: number; onChange: (newValue: number) => void }) => {
    const [inputValue, setInputValue] = useState(formatDecimal(value));
    
    useEffect(() => {
      setInputValue(formatDecimal(value));
    }, [value]);
  
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      
      if (/^[0-9]*\.?[0-9]*$/.test(newValue)) {
        setInputValue(newValue);
        
        const numValue = parseFloat(newValue);
        if (!isNaN(numValue)) {
          onChange(Math.abs(numValue));
        }
      }
    };
  
    const handleBlur = () => {
      let numValue = parseFloat(inputValue) || 0;
      numValue = Math.abs(numValue); 
      setInputValue(formatDecimal(numValue));
      onChange(numValue);
    };
  
    return (
      <div style={{ 
        cursor: "pointer",
        display: "flex",
        justifyContent: "flex-end",
        width: "100%",
        borderBottom: "1px solid #909090",
        paddingBottom: "2px",
      }}>
        <span style={{ 
          marginRight: "4px",
        }}>
          {ConfigDictionary.currency}
        </span>
        <input
          type="text"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === '-') {
              e.preventDefault();
            }
          }}
          style={{
            border: "none",
            background: "transparent",
            width: "60px",
            textAlign: "right",
            color: "#909090",
            fontSize: "13px",
            fontWeight: 500,
            outline: "none",
            padding: 0,
            margin: 0
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    );
  }; 

  const getFlagging = (rowData: any) => {
    if(rowData?.eInvoiced) return <GstFlagging />
    else if(rowData?.cancelledInvoice) return <CancelledInvoiceFlagging />
    return <></>
  }

  const handleClickEdit = (rowData: any) => {
    dispatch(setSelectedRowToEdit(rowData));

    if (insideSaleReturns) {
      navigate("editQuantity");
      logAnalyticsEvent("claim_edit_initiated", {
        distributor_id: JSON.parse(localStorage.authContext).user.loginId,
        claim_type: "Damage Returns",
        claim_subtype: "Sale Returns"
      });
      return;
    }

    dispatch(setOpen(true));
  };

  const columnVisibility: { [key: string]: boolean } =
    widthFixedColumns.columns.reduce((acc, columnMetaData) => {
      acc[columnMetaData.mappedValue] = columnMetaData?.visibility ?? true;
      return acc;
    }, {} as { [key: string]: boolean });

  const CustomNoRowsOverlay = () => {
    return (
      <Box
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <span>{searchedValue ? "No Search results found." : noDataMessage ? noDataMessage : "No data found."}</span>
      </Box>
    );
  };

  const RenderRowStatus = (params: GridRenderCellParams, type: BulkType = "invoice", mappedValue?: string) => {
    const rowStatus: string  = mappedValue ? params.row[mappedValue] : params.row.status ;
    
    switch (true) {
      case renderBulkStatus(rowStatus):
        return <RenderBulkStatus status={rowStatus} type={type} />
      default:
        return <OrderStatusCell
          {...params}
          orderStatusConfig={orderStatusConfig ?? []}
          statusId={rowStatus}
          RenderFlagging={getFlagging(params.row)}
        />
    }
  }

  const columns: GridColDef[] = useMemo(
    () =>
      widthFixedColumns.columns.map((columnMetaData) => {
        const commonProps: Partial<GridColDef> = {
          disableColumnMenu: true,
          field: columnMetaData.mappedValue,
          headerName: columnMetaData.headerName,
          sortable: false,
          width: columnMetaData.width,
          headerAlign: columnMetaData.headerAlign,
          align: columnMetaData.align,
          renderHeader: (params: GridColumnHeaderParams) => {
            const headerParts = columnMetaData.headerParts || {
              top: columnMetaData.headerName,
              bottom: "",
            };
            return (<>
              <Tooltip title={columnMetaData.headerName}>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    textAlign: columnMetaData.headerAlign,
                    lineHeight: "1.2",
                    display: "flex",
                    alignItems: "center",
                    cursor: columnMetaData.sortKey ? "pointer" : "default",
                    userSelect: columnMetaData.sortKey ? "none" : "text",
                  }}
                  onClick={(e) => {
                    if (!columnMetaData.sortKey) return
                    e.stopPropagation();
                    const newSort = handleSortChange(columnMetaData.sortKey, columnMetaData.sortType);
                    setSortByColumn?.(newSort);
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div>{headerParts.top}</div>
                    {headerParts.bottom && <div>{headerParts.bottom}</div>}
                  </div>
                  {columnMetaData.hasEditPenSvg && (
                    <div style={{ marginLeft: "5px" }}>
                      <EditPen style={{ width: "9px", paddingTop: "5px" }} />
                    </div>
                  )}
                  {columnMetaData.sortKey && (
                    <div
                      style={{ marginLeft: "6px", cursor: "pointer" }}
                    >
                      {sortConfig.key === columnMetaData.sortKey ? (
                        sortConfig.direction === "asc" ? (
                          <RenderSvg name="ascOrderSVG"/>
                        ) : sortConfig.direction === "desc" ? (
                          <RenderSvg name="descOrderSVG"/>
                        ) : (
                          <RenderSvg name="neutralOrderSVG"/>
                        )
                      ) : (
                          <RenderSvg name="neutralOrderSVG"/>
                      )}
                    </div>
                  )}
                </div>
              </Tooltip>
              {columnMetaData.hasInfoTooltip && (
                <Tooltip
                  title={columnMetaData.infoTooltipMessage}
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#FFFFFF !important",
                        color: "#000000 !important",
                        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1) !important",
                        fontWeight: 400,
                        maxWidth: "180px",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#FFFFFF !important",
                      },
                    },
                  }}
                >
                  <InfoIcon
                    style={{
                      fontSize: "16px",
                      marginLeft: "8px",
                      color: "#F29C38",
                      cursor: "pointer",
                      alignSelf: "flex-start",
                    }}
                  />
                </Tooltip>
              )}
            </>
            );
          },
        };

        if (columnMetaData?.cellRenderElement) {
          return {
            ...commonProps,
            renderCell: columnMetaData.cellRenderElement
          } as GridColDef;
        }

        if (columnMetaData.mappedValue === "creationTime") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => {
              return dayjs(params.value).format("DD-MM-YYYY HH:mm:ss"); // Date formatting
            },
          } as GridColDef;
        }

        if (
          columnMetaData.headerName === "Outlet Name" &&
          columnMetaData.wrap
        ) {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div className="outlet-name-container">
                {params.row.outletName}
              </div>
            ),
          } as GridColDef;
        } 

        if( columnMetaData.headerName === "Outlet Name & Code" && columnMetaData?.keyAccountShow ){
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
                <div style={{ flex: 1 }}>
                  <Tooltip title={columnMetaData?.truncateText ? params?.row?.outletName : ``} >
                    <div>{params?.row?.outletName}</div>
                  </Tooltip>
                  <div style={{display: "flex", alignItems:"center", gap:"5px"}}>
                    <Tooltip title={columnMetaData?.truncateText ? params?.row?.outletCode : ``} >
                      <div style={{fontSize: "11px", fontWeight: 400}}>{params?.row?.outletCode}</div>
                    </Tooltip>
                    {params?.row?.isKeyAccount && <StarsRoundedIcon
                      sx={{
                        color: "#FDB42C",
                        background: "#FFE8BF",
                        height: "15px",
                        width: "15px",
                        borderRadius: "50%",
                      }}
                    />}
                  </div>
                </div>
            ),
          } as GridColDef;
        }

        if( columnMetaData?.headerName === "Outlet Name & Code" && columnMetaData?.showOutletGstinSvg ){
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
                <div style={{ flex: 1 }}>
                  <Tooltip title={columnMetaData?.truncateText ? params?.row?.outletName : ``} >
                    <div>{truncateText(params?.row?.outletName,20)}</div>
                  </Tooltip>
                  <div style={{display: "flex", alignItems:"center", gap:"5px"}}>
                    <Tooltip title={columnMetaData?.truncateText ? params?.row?.outletCode : ``} >
                      <div style={{fontSize: "11px", fontWeight: 400}}>{truncateText(params?.row?.outletCode, 15)}</div>
                    </Tooltip>
                    { params?.row?.outletHasGstin && 
                     <div style={{ height: "12px" , marginBottom:"4px"}}>
                     <RenderSvg name="gstIcon" />
                   </div>
                    }
                  </div>
                </div>
            ),
          } as GridColDef;
        }

        if (columnMetaData.mappedValue.toLowerCase() === "skucode") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                {(columnMetaData?.hasInfoTooltip || columnMetaData?.hasFocIcon)? (
                  <Tooltip title={params?.row?.skuCode || ""} arrow>
                    <span>{truncateText(params?.row?.skuCode, 12)}</span>
                  </Tooltip>
                ) : (
                  <span title={params?.row?.skuCode}>{params?.row?.skuCode}</span>
                )}
                {columnMetaData?.hasFocIcon && params?.row?.stockType === "foc" && (
                  <RenderSvg name="focIcon" />
                )}
                {params?.row?.isNonEligible && (
                  <ErrorIcon
                    style={{
                      fontSize: "16px",
                      color: "#F44336",
                      alignSelf: "center",
                    }}
                  />
                )}
              </div>
            )
          } as GridColDef;
        }

        if (columnMetaData.headerName === "Discounts & Promotions") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div
                className="view-discounts-container"
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setSelectedInvoiceRow(params.row)); 
                  dispatch(setDiscountModalState(true));
                }}
              >
                <span>View Discounts</span>
                <CircledRight style={{ width: "10px" }} />
              </div>
            ),
          } as GridColDef;
        }

        if (
          (columnMetaData.headerName?.toLowerCase() === "invoice no. & date" ||
            columnMetaData.headerName?.toLowerCase() === "invoice no.") &&
          columnMetaData?.invoiceNumStyling
        ) {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div className="invoice-no-and-date">
                <span className="inv-number">{params.row.invoiceNumber}</span>
                {columnMetaData.headerName.toLowerCase() !== "invoice no." && (
                  <span className={"inv-date"}>{params.row.invoiceDate}</span>
                )}
              </div>
            ),
          } as GridColDef;
        }

        if (columnMetaData.mappedValue?.toLowerCase() === 'creditnoteused') {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap" }}>
                {params?.row?.creditNoteUsed?.map((creditNote: string, index: number) => (
                  <span key={index} style={{ color: '#2568E5', fontSize: '13px', textDecoration: 'underline', cursor: 'pointer'}}
                  onClick={(e) => {
                      e.stopPropagation();
                      handleClickCreditNote && handleClickCreditNote(creditNote)}
                    }
                  >
                    {creditNote}
                    {index < params.row.creditNoteUsed.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            ),
          } as GridColDef;
        }
       
        if (columnMetaData?.type === "dropdown") {
          return {
            ...commonProps,
            field: columnMetaData.field, // make sure this exists in your column metadata
            renderCell: (params: GridRenderCellParams) => {
              const isManual = params.row[params.field].toUpperCase() === "MANUAL";
              
              return (
                <div style={{display: "flex", flexDirection: "column"}}>
                <Select
                  value={params.row[params.field] ?? ""}
                  onClick={(e) => e.stopPropagation()} // prevent row selection
                  onChange={(e) => {
                    columnMetaData.handleDropDownSelect?.(
                      e.target.value as string,
                      params.row
                    );
                  }}
                  size="small"
                  fullWidth
                  displayEmpty
                  sx={{
                    fontSize: "12px",
                    height: "25px",
                    backgroundColor: isManual ? "#F6FDFF" : "#f5f5f5",
                    borderRadius: "20px",
                    border: `1px solid ${isManual ? "#33778A" : "#e0e0e0"}`,
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none", // Remove default border
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: `1px solid ${isManual ? "#33778A" : "#e0e0e0"}`,
                      borderRadius: "20px",
                    },
                    "& .MuiSelect-select": {
                      padding: "8px 12px",
                      color: isManual ? "#33778A" : "#666",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiSelect-icon": {
                      color: isManual ? "#33778A" : "#666",
                      right: "8px",
                    },
                    "&:hover": {
                      backgroundColor: isManual ? "#F0FCFF" : "#eeeeee",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "12px",
                        marginTop: "4px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                       "& .MuiList-root": {
                          padding: 0, // remove list padding
                        },
                        width: "100px",
                        "& .MuiMenuItem-root": {
                          fontSize: "12px",
                          padding: "8px 12px",
                          color: "#666",
                          "&:hover": {
                            backgroundColor: "#f0f0f0",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "#e3f2fd",
                            color: "#666666",
                            "&:hover": {
                              backgroundColor: "#e3f2fd",
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {columnMetaData.dropdownValues?.map((val: string, idx: number) => (
                    <MenuItem key={idx} value={val}>
                      {val}
                    </MenuItem>
                  ))}
                </Select>
                {params?.row?.allocationMethod.toUpperCase() === "MANUAL" && 
                  <span style={{fontSize: "10px", marginLeft: "8px"}}>
                    {params?.row?.hasCompletedAllocation ?

                      <span style={{color: "#00A392"}}>Action Completed</span>
                      :
                      <span style={{color: "#CC921F"}}>Action Pending</span>
                    }
                  </span>}
                </div>
              );
            },
          } as GridColDef;
        }

        
        if (columnMetaData?.type === "popOver") {
          return {
            ...commonProps,
            field: columnMetaData.field,
            renderCell: (params: GridRenderCellParams) => {
              // params.row.sku is expected to be the structured skuSummaryList for that row
              return <ReplacementPopoverCell  sku={params.row.batchCode} replacements={params.row.replacementSkuAllocated}/>;
            },
          } as GridColDef;
        }

        if (columnMetaData?.type === "viewDropDown") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => {
              const outlets = params.row.outletList ?? [];
              const outletCount = outlets.length;

              return (
                <Select
                  value=""
                  onClick={(e) => e.stopPropagation()} // prevent row selection
                  size="small"
                  fullWidth
                  displayEmpty
                  IconComponent={(iconProps) => (
                    <KeyboardArrowDownIcon
                      {...iconProps}
                      sx={{ fontSize: "16px", marginRight: "2px" }} // 🔹 smaller chevron
                    />
                  )}
                  sx={{
                    fontSize: "13px",
                    height: "25px",
                    fontWeight: 500,
                    backgroundColor: "transparent", // 🔹 no background
                    border: "none", // 🔹 no border
                    color: "#909090", // 🔹 darker text
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiSelect-select": {
                      padding: "0px 18px 0px 0px",
                      fontWeight: 500,
                      color: "#909090", // 🔹 stronger text
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiSelect-icon": {
                      color: "#909090",
                      fontSize: "16px", // 🔹 reduced size
                      right: "10px",
                    },
                  }}
                 MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: "8px",
                      marginTop: "2px",
                      maxHeight: 200,
                      maxWidth: 300,
                      overflowX: "hidden",
                      overflowY: "auto",
                      "& .MuiMenuItem-root": {
                        fontSize: "12px",
                        padding: "6px 12px",
                        color: "#909090",     // darker text
                        fontWeight: 500,
                      },
                    },
                  },
                }}
                  renderValue={() => `${outletCount} Outlet${outletCount > 1 ? "s" : ""}`}
                >
                  {outlets.map((outlet: any, idx: number) => {
                    const text = `${outlet.outletName} (${outlet.outletCode})`;

                    return (
                      <Tooltip key={idx} title={text} arrow>
                        <MenuItem value={outlet.outletCode}>
                          <Box
                            sx={{
                              maxWidth: 240,          // 🔹 must set width constraint
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {text}
                          </Box>
                        </MenuItem>
                      </Tooltip>
                    );
                  })}
                </Select>
              );
            },
          } as GridColDef;
        }



        if (columnMetaData.mappedValue.toLowerCase() === "removeinvoice" || columnMetaData.mappedValue.toLowerCase() === "removepurchasereturnsclaims") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <DeleteIcon
                style={{ width: "16px", cursor: "pointer" }}
                onClick={(e) => 
                  {
                    e.stopPropagation();
                    handleDeleteRow?.(params.row)}
                  }
              />
            ),
          } as GridColDef;
        }

        if (
          (columnMetaData.headerName.toLowerCase() === "order source" || columnMetaData.mappedValue.toLowerCase() === "outletsource") &&
          Object.keys(orderSourceConfig).length > 0
        ) {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div
                style={{
                  background: manageSecondaryOrderV2 ? " " :`linear-gradient(to right, ${
                    orderSourceConfig[params.value]?.gradientColor ?? "#FFF3DB"
                  }, transparent)`,
                  color: manageSecondaryOrderV2 ? "#7C7C7C" : orderSourceConfig[params.value]?.textColor ?? "#CC921F",
                  padding: "3px 12px 3px 7px",
                  borderRadius: "4px",
                  minWidth: "70px",
                }}
              >
                {params.value}
              </div>
            ),
          } as GridColDef;
        }

        if (columnMetaData.mappedValue.toLowerCase() === "returnorderno") {
          if( columnMetaData.noHyperlinkStyle && columnMetaData.cellRenderElement){
            return {
              ...commonProps,
              renderCell: columnMetaData.cellRenderElement,
            } as GridColDef;
          }
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => {
              const returnOrderNo = Object.keys(params.row.returnOrderNo || {});
              return (
                <Tooltip
                  title={<RenderTooltipContent items={returnOrderNo} title="Return Order No" handleRedirect={redirectUrlFunction}/>}
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#FFFFFF !important",
                        color: "#000000 !important",
                        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1) !important",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#FFFFFF !important",
                      },
                    },
                  }}
                >
                  <div style={{ cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {returnOrderNo[0]?.slice(0, 15)}…
                    {returnOrderNo.length > 1 && (
                      <span style={{ color: "#2568E5", textDecoration: "underline" }}>
                        +{returnOrderNo.length - 1}
                      </span>
                    )}
                  </div>
                </Tooltip>
              );
            },
          } as GridColDef;
        }

        if (columnMetaData.mappedValue.toLowerCase() === "invoices" && columnMetaData.renderToolTip) {
          if( columnMetaData.noHyperlinkStyle && columnMetaData.cellRenderElement){
            return {
              ...commonProps,
              renderCell: columnMetaData.cellRenderElement,
            } as GridColDef;
          }
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => {
              const invoiceNo =params.row.invoices; 
              if (!invoiceNo.length) {
                return <span>-</span>;
              }
              return (
                <Tooltip
                  title={<RenderTooltipContent items={invoiceNo} title="Invoice" />}
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#FFFFFF !important",
                        color: "#000000 !important",
                        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1) !important",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#FFFFFF !important",
                      },
                    },
                  }}
                >
                  <div style={{ cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {invoiceNo[0]?.slice(0, 15)}{invoiceNo?.length ? '…' : ''}
                    {` `}
                    {invoiceNo.length > 1 && (
                      <span style={{ color: "#2568E5", textDecoration: "underline", fontSize: columnMetaData.defaultFont ? "inherit" : "11px" }}>
                       +{invoiceNo.length - 1}
                      </span>
                    )}
                  </div>
                </Tooltip>
              );
            },
          } as GridColDef;
        }

        if (columnMetaData.mappedValue.toLowerCase() === "skus") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => {
              const skus = params.row.skus || [];
              if (skus.length === 0) {
                return <div>-</div>;
              }
              return (
                <Tooltip
                  title={<RenderTooltipContent items={skus} title="SKU Code & Name" />}
                  placement="right"
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "#FFFFFF !important",
                        color: "#000000 !important",
                        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1) !important",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#FFFFFF !important",
                      },
                    },
                  }}
                >
                  <div style={{ cursor: "pointer", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {skus[0]?.slice(0, 15)}…
                    {skus.length > 1 && (
                      <span style={{ color: "#2568E5", textDecoration: "underline" }}>
                        +{skus.length - 1}
                      </span>
                    )}
                  </div>
                </Tooltip>
              );
            },
          } as GridColDef;
        }

        if (columnMetaData.mappedValue.toLowerCase() == "skudetailseyeicon") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) =>
              params.row?.status?.toLowerCase() == "rejected" &&
                params.row?.parentStatus?.toLowerCase() ==
                "partially approved" ? (
                <div
                  style={{ cursor: "pointer", paddingRight: "26px" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClickEdit(params.row);
                  }}
                >
                  <RenderSvg name={"eyeIcon"} />
                </div>
              ) : (
                <div></div>
              ),
          } as GridColDef;
        }
        if (
          columnMetaData.headerName.toLowerCase() === "" &&
          columnMetaData?.type?.toLowerCase() === "edit" ||
          columnMetaData?.type?.toLowerCase() === "view"
        ) {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div
                style={{
                  cursor: "pointer",
                  paddingRight: "26px",
                  color: "#227C9D",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onEditClick) {
                    onEditClick(params.row);
                  } else {
                    dispatch(setSelectedSecondarySchemesRow(params.row));
                    navigate("invoiceDetails");
                  }
                }}
              >
                {columnMetaData?.type?.toLowerCase() === "edit" ? <RenderSvg name={"viewAndEditIcon"} /> : <RenderSvg name={"eyeIcon"} /> }
              </div>
            ),
          } as GridColDef;
        }

        if (columnMetaData.mappedValue.toLowerCase() === "removeInvoice" || columnMetaData.mappedValue.toLowerCase() === "removepurchasereturnsclaims") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => <DeleteIcon />,
          } as GridColDef;
        }

        if (columnMetaData.mappedValue.toLowerCase() === "eyeicon") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div
                style={{ cursor: "pointer", paddingRight: "26px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (redirectUrl) {
                    navigate(`/dashboard${redirectUrl}`, {
                      state: params.row,
                    });
                  }
                }}
              >
                <RenderSvg name={"eyeIcon"} />
              </div>
            ),
          } as GridColDef;
        }

        if (columnMetaData.mappedValue.toLowerCase() === "batchid") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div>
                {params.row.batchId.includes("unassigned")
                  ? "Unassigned Qty."
                  : params.row.batchId}
              </div>
            ),
          } as GridColDef;
        }

        if (
          (columnMetaData.headerName.toLowerCase() === "quantity" &&
            columnMetaData.type?.toLowerCase() === "stocks") ||
          columnMetaData.mappedValue.toLowerCase() === "currentstocklevel"
        ) {
          return {
            ...commonProps,
            headerClassName: "MuiDataGridColumnHeaders",
            renderCell: (params: GridRenderCellParams) => {
              return <MultiQtyCell {...getCasePieceSumProps(params.row)} otherUnitName={params?.row.otherUnitName}/>;
            },
          } as GridColDef;
        } else if (
          columnMetaData.headerName.toLowerCase() === "stock qty" ||
          columnMetaData.headerName.toLowerCase() === "updated qty"
        ) {
          return {
            ...commonProps,
            headerClassName: "MuiDataGridColumnHeaders",
            renderCell: (params: GridRenderCellParams) => {
              const pcQty = params.row?.updatedPieceQuantity;
              const csQty = params.row?.updatedCaseQuantity;
              const otherQty = params.row?.updatedOtherQuantity;
              return <MultiQtyCell pcQty={pcQty} csQty={csQty} otherQty={otherQty} otherUnitName={params.row?.otherUnitName}/>;
            },
          } as GridColDef;
        } else if (
          (columnMetaData.headerName.toLowerCase() === "stock from" ||
            columnMetaData.headerName.toLowerCase() === "stock to") &&
          columnMetaData.type?.toLowerCase() === "stocks"
        ) {
          return {
            ...commonProps,
            headerClassName: "MuiDataGridColumnHeaders",
            renderCell: (params: GridRenderCellParams) => {
              const {
                currentPieceStockQty,
                currentCaseStockQty,
                updatedPieceStockQty,
                updatedCaseStockQty,
                currentOtherStockQty,
                updatedOtherStockQty
              } = params.row;
              if (columnMetaData.mappedValue == "currentStock") {
                return (
                  <MultiQtyCell
                    pcQty={currentPieceStockQty}
                    csQty={currentCaseStockQty}
                    otherQty={currentOtherStockQty}
                    otherUnitName={params?.row.otherUnitName}
                  />
                );
              }

              return (
                <MultiQtyCell
                  pcQty={updatedPieceStockQty}
                  csQty={updatedCaseStockQty}
                  otherQty={updatedOtherStockQty}
                  otherUnitName={params?.row.otherUnitName}
                />
              );
            },
          } as GridColDef;
        }
        else if (columnMetaData.type?.toLowerCase() === "viewprimaryorderconfigs") {
          const parts = String(columnMetaData.mappedValue)
            .split(",")
            .map((part) => part.trim());
          return {
            ...commonProps,
            headerClassName: "MuiDataGridColumnHeaders",
            renderCell: (params: GridRenderCellParams) => (
              <>
                <MultiQtyCell
                  viewprimaryorderconfigs={true}
                  csQty={params.row[parts[0]]}
                  pcQty={params.row[parts[1]]}
                  otherQty={params.row[parts[2]]}
                 
                />
               
              </>
            ),
          } as GridColDef; 
          }
        
        else if (renderMultiQtyCell(columnMetaData)) {
          const parts = String(columnMetaData.mappedValue)
            .split(",")
            .map((part) => part.trim());
          return {
            ...commonProps,
            headerClassName: "MuiDataGridColumnHeaders",
            renderCell: (params: GridRenderCellParams) => (
              <>
                {columnMetaData.isEditableMappedValue && params.row[columnMetaData.isEditableMappedValue] ? 
                <MultiQtyInputCell
                  onClick = {(e) => e.stopPropagation()}
                  csQty={params.row[parts[0]]}
                  pcQty={params.row[parts[1]]}
                  otherQty={params.row[parts[2]]}
                  onQtyChange={(qtyObject:any)=>{
                    params.row[parts[0]]=qtyObject.csQty;
                    params.row[parts[1]]=qtyObject.pcQty;
                    params.row[parts[2]]=qtyObject.otherQty;
                    return;
                  }}
                />
                :
                (columnMetaData?.includeStockCheck) ?
                <MultiQtyCell
                  csQty={params.row[parts[0]]}
                  pcQty={params.row[parts[1]]}
                  otherQty={params.row[parts[2]]}
                  hasEditButton={
                    columnMetaData.headerName.toLowerCase() === "damage qty" ||
                    false
                  }
                  handleClickEdit={() => handleClickEdit(params.row)}
                  handleClickEditPurchaseReturnsRow={() =>
                    insidePurchaseReturns &&
                    handleClickEditPurchaseReturnsRow!(params.row)
                  }
                  insidePurchaseReturns={insidePurchaseReturns}
                  otherUnitName={params.row?.otherUnitName}
                  isStockExceeded={(params?.row?.stockQuantityExceed) ? true : false}
                  insideManageOrder={insideManageOrder}
                /> 
                :
                (columnMetaData?.showOverSoldQuantity) ?
                <div style={{display: "flex", flexDirection: "column"}}>
                  <MultiQtyCell
                    csQty={params.row[parts[0]]}
                    pcQty={params.row[parts[1]]}
                    otherQty={params.row[parts[2]]}
                    hasEditButton={
                      columnMetaData.headerName.toLowerCase() === "damage qty" ||
                      false
                    }
                    handleClickEdit={() => handleClickEdit(params.row)}
                    handleClickEditPurchaseReturnsRow={() =>
                      insidePurchaseReturns &&
                      handleClickEditPurchaseReturnsRow!(params.row)
                    }
                    insidePurchaseReturns={insidePurchaseReturns}
                    otherUnitName={params.row?.otherUnitName}
                    isStockExceeded={false}
                    insideManageOrder={insideManageOrder}
                  /> 
                  {
                    (columnMetaData?.showOverSoldQuantity && params?.row?.stockQuantityExceed) ?
                    <span style={{fontSize: "8px", color: "red"}}>{`Oversold: ${params.row.stockExceedingCases} Cs, ${params.row.stockExceedingOthers} Oth, ${params.row.stockExceedingPieces} Pc`}</span>
                    :
                    <></>
                  }
                </div>
                
                : (params.row?.includeQty === undefined || params.row?.includeQty === true) ? 
                <MultiQtyCell
                  csQty={params.row[parts[0]]}
                  pcQty={params.row[parts[1]]}
                  otherQty={params.row[parts[2]]}
                  hasEditButton={
                    columnMetaData.headerName.toLowerCase() === "damage qty" ||
                    false
                  }
                  highlightBorder={ columnMetaData.headerName === "Allocated Quantity" ? params.row.borderColor ?? undefined : undefined}
                  handleClickEdit={() => handleClickEdit(params.row)}
                  handleClickEditPurchaseReturnsRow={() =>
                    insidePurchaseReturns &&
                    handleClickEditPurchaseReturnsRow!(params.row)
                  }
                  insidePurchaseReturns={insidePurchaseReturns}
                  otherUnitName={params.row?.otherUnitName}
                  isStockExceeded={(insideManageOrder && params?.row?.stockQuantityExceed) ? true : false}
                  insideManageOrder={insideManageOrder}
                /> : 
                <div>
                {params.row.totalCase}
                </div>
                }
                { columnMetaData.isEditable && redirectUrl && redirectUrlFunction ?
                  <div>
                    <button className="MultiQtyCell_edit-button" onClick={(e) => 
                      {
                        e.stopPropagation();
                        redirectUrlFunction(redirectUrl, params.row)}
                      }
                    >
                      <RenderSvg name={"viewAndEditIcon"} />
                    </button>
                  </div> :<></>
                }
                
              </>
            ),
          } as GridColDef;
        } else if (columnMetaData.mappedValue === "stockTypeTransfer") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => {
              // Split the value once outside the JSX
              const [from, to] = params.row.stockTypeTransfer
                .split(",")
                .map((part: string) => part.trim());

              // Render the StockTypeTransferCell with the split values
              return <StockTypeTransferCell from={from} to={to} />;
            },
          } as GridColDef;
        } else if (columnMetaData.headerName.toLowerCase() === "expiry date") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <ExpiryDateCell
                expiryDate={params.row.expiryDate}
                shelfLife={params.row.shelfLife}
              />
            ),
          } as GridColDef;
        } else if (columnMetaData.headerName.toLowerCase() === "amount" || columnMetaData.includeCurrenySymbol === true || columnMetaData.headerName.toLowerCase() === "ptr" || columnMetaData.headerName.toLowerCase() === "return amount" || columnMetaData.headerName.toLowerCase() === "gst amount" || columnMetaData.headerName.toLowerCase() === "tcs amount" || columnMetaData.headerName.toLowerCase() === "discount amount" || columnMetaData.headerName.toLowerCase() === "final amount" || columnMetaData.headerName.toLowerCase() === "gst amt." || columnMetaData.amountDisplay) {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => {
              if (columnMetaData.isEditable && columnMetaData.headerName.toLowerCase() === "final amount") {
                return (
                  <EditableFinalAmountCell
                    value={params.row.finalAmount}
                    onChange={(newValue) => {
                      params.row.finalAmount = newValue;
                    }}
                  />
                );
              }

              return (
                <div>
                  {ConfigDictionary.currency}{`\u200A`}
                  {formatDecimal(+params.row[columnMetaData.mappedValue])}
                </div>
              );
            },
          } as GridColDef;
        }else if (
          columnMetaData.headerName.toLowerCase() === "upload status"
        ) {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <UploadStatusCell status={params.row?.status}/>
            )
          } as GridColDef;
        } else if (
          columnMetaData.headerName.toLowerCase() === "status" ||
          columnMetaData.headerName.toLowerCase() === "order status" ||
          columnMetaData.mappedValue.toLowerCase() === "status" ||
          columnMetaData.headerName.toLowerCase() === "payment approval status"
        ) {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => {
              
              return (
                RenderRowStatus(params, bulkType, columnMetaData.mappedValue)
              )
            }
          } as GridColDef;
        } else if (
          columnMetaData.headerName.toLowerCase() === "type" ||
          columnMetaData.headerName.toLowerCase() === "outletType"
        ) {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <OrderTypeCell type={params.row?.outletType}/>
            )
          } as GridColDef;
        } else if (columnMetaData.mappedValue.toLowerCase() === "tostatus") {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <OrderStatusCell
                {...params}
                orderStatusConfig={orderStatusConfig ?? []}
                statusId={params.row?.toStatus}
              />
            ),
          } as GridColDef;
        } else if (columnMetaData.mappedValue.includes(",")) {
          const parts = String(columnMetaData.mappedValue)
            .split(",")
            .map((part) => part.trim());

          const truncate = columnMetaData?.truncateText;

          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div>
                {parts.map((part: string, index: number) => (
                  <Tooltip title={truncate ? params?.row?.[part] : ``}>
                    <div
                      className="truncate_text"
                      key={index}
                      style={{
                        ...(truncate && {
                          maxWidth: `${0.9 * columnMetaData?.width}px`,
                        }),
                        fontSize: index === parts.length - 1 ? "12px" : "13px", 
                        fontWeight: index === parts.length - 1 ? "400" : "500", 

                      }}
                    >
                      {params?.row?.[part]}
                    </div>
                  </Tooltip>
                ))}
              </div>
            ),
          } as GridColDef;
        } else if (
          columnMetaData.headerName === "GRN Status(if any)" ||
          columnMetaData.headerName === "GRN Status"
        ) {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => {
              if (params?.row?.status?.toUpperCase() === "ORDERED" || params?.row?.hideGRNStatusPendingOrder)
                return <></>;
              if (
                params?.row?.status?.toUpperCase() === "INTRANSIT" &&
                !params?.row?.invoiceNumber
              )
                return <></>;
              return (
                <>
                  {params.row.grnStatus?.toUpperCase() === "OPEN" ? (
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        borderRadius: "6px",
                        textTransform: "none",
                        padding: "5px 4px",
                        fontSize: "12px",
                        minWidth: "4.5rem",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/dashboard/${openButtonNavigateUrl}/viewGrnDetails/${encodeURIComponent(params?.row?.invoiceNumber)}`,
                          {
                            state: {
                              row: params.row,
                            },
                          }
                        );
                      }}
                    >
                      Open
                      <CallMadeIcon style={{ fontSize: "15px" }} />
                    </Button>
                  ) : (
                    <OrderStatusCell
                      {...params}
                      orderStatusConfig={grnStatusConfig ?? []}
                      statusId={params?.row?.grnStatus}
                      solidColors={false}
                      isARS={params?.row?.source?.toLowerCase() === "ars"}
                    />
                  )}
                </>
              );
            },
          } as GridColDef;
        } else if (columnMetaData?.truncateText) {
          return {
            ...commonProps,
            renderCell: (params: GridRenderCellParams) => (
              <div>
                <Tooltip title={params?.row?.[columnMetaData?.mappedValue]}>
                  <div
                    className="truncate_text"
                    style={{ maxWidth: `${0.9 * columnMetaData?.width}px` }}
                  >
                    {params?.row?.[columnMetaData?.mappedValue]}
                  </div>
                </Tooltip>
              </div>
            ),
          } as GridColDef;
        } else {
          return commonProps as GridColDef;
        }
      }),
    [checkedIdArray, widthFixedColumns, orderStatusConfig, grnStatusConfig, isDownload, disabledRowIds]
  );

  const [openGenericModal, setOpenGenericModal] = useState<boolean>(false);
  const [popupType, setPopupType] = useState<popupType>("Alert");
  const [genericMessage, setGenericMessage] = useState<string>("");
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const addActionAttributes = (
    field: string,
    headerName: "",
    action: (params: GridRenderCellParams) => JSX.Element
  ) => {
    const commonAttributes: GridColDef = {
      field,
      headerName,
      sortable: false,
      minWidth: 50,
      maxWidth: 50,
      flex: 0.5,
      headerAlign: "left",
      align: "center",
      disableColumnMenu: true,
      renderCell: action,
    };
    if (field == "Checkbox") {
      columns.unshift(commonAttributes);
    } else {
      columns.push(commonAttributes);
    }
  };

  const isDisplayEyeIcon = (status: string, showIcon?: boolean) => {
    const orderStatus = status?.toUpperCase();
    const designationBasedOrderEdit = ConfigDictionary?.disableOrderEditDesignationBased?.enable ? ConfigDictionary?.disableOrderEditDesignationBased?.enable : false;
    return (
      (designationBasedOrderEdit && disableOrderEditDesignationBasedChild) || 
      showIcon ||
      orderStatus === "REJECTED" ||
      orderStatus === "CREDIT NOTE ISSUED" ||
      orderStatus === "INVOICED" ||
      orderStatus === "CANCELLED" ||
      orderStatus === "APPROVED" || 
      orderStatus === "PARTIALLY APPROVED" || 
      orderStatus === "REQUESTED" || 
      orderStatus === "CREDIT NOTE ISSUED" ||
      orderStatus === "DELIVERED" ||
      orderStatus === "PARTIAL DELIVERED" ||
      orderStatus === "OUT FOR DELIVERY" || 
      orderStatus === "PARTIALLY INVOICED"
    );
  };

  const get_View_Edit_RedirectUrl = (params: GridRenderCellParams) => {
    const row = params?.row;
    if (row?.checkOrderStatus) {
      return `/dashboard${redirectUrl}${params.row.id}`;
    } else if (handleClickBulk) {
      return `/dashboard${redirectUrl}${params.row.orderId}`;
    }else if(row?.redirectViewGRNicon){
      return `/dashboard${redirectUrl}${encodeURIComponent(params.row.invoiceNumber)}`;
    }else if (insideSecondarySchemes) {
      return `/dashboard${redirectUrl}${params.row.orderNo}`;
    }
    else {
      return `/dashboard${redirectUrl}${params.row.id}${
        params.row.extraParams ?? ``
      }`;
    }
  };

  // Helper function for row click redirection that doesn't require GridRenderCellParams
  const getRowClickRedirectUrl = (row: any) => {
    if (row?.checkOrderStatus) {
      return `/dashboard${redirectUrl}${row.id}`;
    } else if (handleClickBulk) {
      return `/dashboard${redirectUrl}${row.orderId}`;
    }else if(row?.redirectViewGRNicon){
      return `/dashboard${redirectUrl}${encodeURIComponent(row.invoiceNumber)}`;
    }else if (insideSecondarySchemes) {
      return `/dashboard${redirectUrl}${row.orderNo}`;
    }else {
      return `/dashboard${redirectUrl}${row.id}${
        row.extraParams ?? ``
      }`;
    }
  };

  // Helper function to determine which icon to show based on component context and status
  const getActionIconName = (params: GridRenderCellParams, isOnSalesReturnScreen: boolean = false) => {
    if (params?.row?.editIcon) {
      return "newViewAndEditIcon";
    }
    else if(params?.row?.companyEditIcon){
      return "companyEditIcon";
    }
    
    if (isOnSalesReturnScreen) {
      const status = params?.row?.status?.toUpperCase();
      if (status === "APPROVED") {
        return "creditNoteIcon";
      } else if (status === "REJECTED" || status === "CREDIT NOTE ISSUED") {
        return "eyeIcon";
      } else if (status === "PENDING") {
        return "viewAndEditIcon";
      }
    }
    if ("showEditIcon" in (params?.row ?? {})){
      return (params?.row?.showEditIcon === true)?
      "newViewAndEditIcon":
      ""  
    }
    // Default behavior for non-sales return screens
    return isDisplayEyeIcon(params?.row?.status, Boolean(params?.row?.showEyeIcon))
      ? "newEyeIcon"
      : "newViewAndEditIcon";
  };

  widthFixedColumns.Actions?.forEach((actionName: string) => {
    switch (actionName) {
      case "view":
        addActionAttributes("View/Edit", "", (params: GridRenderCellParams) => {
        if(disabledRowIds.includes(params.id)) return <></>
          return (
            <button
              className="manage_order_table_edit_button"
              style={{display: "flex", alignItems: "center"}}
              disabled={disabledRowIds?.includes(params?.row?.id)}
              onClick={(e) => {
                e.stopPropagation()
                if (onRowClick && insideManualStockAllocation) {
                  if (params?.row?.allocationMethod.toUpperCase() === "MANUAL") onRowClick(params?.row)
                  return;
                }
                if (onEditClick) {
                  onEditClick(params?.row); 
                  return;
                }
                if(redirectUrlFunction){
                  redirectUrlFunction?.(redirectUrl, params.row);
                  return;
                }
                const url = get_View_Edit_RedirectUrl(params);
                navigate(url, {
                  state: {
                    row: params.row,
                    ...locationState
                  },
                });
              }}
            >
              <RenderSvg
                name={getActionIconName(params, onSalesReturnLandingScreen)}
              />
            </button>
          );
        });
        break;
      case "redirect":
        addActionAttributes("redirect", "", (params: GridRenderCellParams) => {
        if(disabledRowIds.includes(params.id)) return <></>
          return (
            <button
              className="manage_order_table_edit_button"
              onClick={(e) => {
                e.stopPropagation();
                if(redirectUrlFunction){
                  redirectUrlFunction(redirectUrl, params.row);
                  return;
                }

                navigate(
                  `/dashboard${redirectUrl}`,
                  {
                    state: {
                      row: params.row,
                    },
                  }
                );
              }}
            >
              <RenderSvg
                name={params.row.redirectIcon ? params.row.redirectIcon : "viewAndEditIcon"}
              />
            </button>
          );
        });
        break;
      case "download":
        addActionAttributes("Download", "", (params: GridRenderCellParams) => {
        if(disabledRowIds.includes(params.id)) return <></>
          if (params?.row?.showDeleteIcon) {
            return (
              <button
                className="manage_order_table_edit_button"
                disabled={disabledRowIds?.includes(params?.row?.id)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDeleteFunction) {
                    onDeleteFunction(params?.row?.id);
                  }
                }}
              >
                <RenderSvg name="deleteIcon" />
              </button>
            );
          }

          const config = {
            reportId: downloadSingleReportConfig?.reportId ?? "",
            filters: (downloadSingleReportConfig?.filters ?? []).map((filter) => {
              const filterValueKey = filter?.key ?? "";
              const filterValue = params?.row?.[filterValueKey] ?? "";
              return { key: filter?.key, value: filterValue };
            }),
          };

          const handleDownload = async () => {
            if (isDownload) return;
            
            try {
              setIsDownload(true);
              const isArs = params?.row?.source?.toLowerCase() === "ars"
              reportDownloadSuccess.current = true
              if (onDownloadGeneric) {
                await onDownloadGeneric(params?.row?.downloadId, setSnackbarOpen);
              } else {
                const startTime = window.performance.now();
                await downloadSingleReport(
                  config,
                  setSnackbarOpen,
                  setIsDownload,
                  setErrorReason,
                  isPrimaryOrder,
                  reportDownloadSuccess
                );
                if(reportDownloadSuccess.current) {
                  const endTime = window.performance.now();
                  if(params?.row?.invoiceNumber) {
                    if (isPrimaryModuleReport){
                      logEvent(analytics, "grn_report_downloaded", {
                        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
                        "grn_id": params?.row?.id ?? null,
                        "download_format": "xlsx",
                        "download_time": (endTime - startTime),
                      });
                    } else {
                      logEvent(analytics, "secondary_invoice_report_downloaded", {
                        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
                        "invoice_id": params?.row?.id ?? null,
                        "download_format": "xlsx",
                        "download_time": (endTime - startTime),
                      });
                    }
                  } else {
                    if (isPrimaryModuleReport){
                      const label = isArs ? "ars_order_id" : "primary_order_id"
                      logEvent(analytics, `${isArs ? "ars_order_report_downloaded" : "primary_order_report_downloaded"}`, {
                        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
                        [label]: params?.row?.id ?? null,
                        "download_format": "xlsx",
                        "download_time": (endTime - startTime),
                      });
                    } else {
                      logEvent(analytics, "secondary_order_report_downloaded", {
                        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
                        "order_id": params?.row?.id ?? null,
                        "download_format": "xlsx",
                        "download_time": (endTime - startTime),
                      });
                    }
                  }
                } else {
                  if(params?.row?.invoiceNumber) {
                    if (isPrimaryModuleReport){
                      logEvent(analytics, "grn_report_download_failed", {
                        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
                        "invoice_id": params?.row?.id ?? null,
                        "error_reason": errorReason,
                      });
                    } else {
                      logEvent(analytics, "secondary_invoice_report_download_failed", {
                        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
                        "invoice_id": params?.row?.id ?? null,
                        "error_reason": errorReason,
                      });
                    }
                  } else {
                    if (isPrimaryModuleReport) {
                      const label = isArs ? "ars_order_id" : "primary_order_id"
                      logEvent(analytics, `${isArs ? "ars_order_report_download_failed" : "primary_order_report_download_failed"}`, {
                        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
                        [label]: params?.row?.id ?? null,
                        "error_reason": errorReason,
                      });
                    } else {
                      logEvent(analytics, "secondary_order_report_download_failed", {
                        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
                        "order_id": params?.row?.id ?? null,
                        "error_reason": errorReason,
                      });
                    }
                  }
                }
              }
            } finally {
              setIsDownload(false);
            }
          };

          return (
            <button
              className="manage_order_table_download_button"
              style={{display: "flex", alignItems: "center"}}
              disabled={disabledRowIds?.includes(params?.row?.id)}
              onClick={(e) => 
                {
                  e.stopPropagation();
                  handleDownload()
                }
              }
            >
              <DownloadIcon />
            </button>
          );
        });
        break;
    }
  });

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setSnackbarOpen(false);
  };

  const CustomFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooter />
        <button
          className="button-save-claim"
          style={{ marginLeft: "auto", width: "140px", height: "39px" }}
          onClick={() => {
            if (onSave) {
              onSave();
            }
            else if (handleDeleteRow && insidePurchaseReturns) {
              navigate("summary");
            } 
            else if (handleDeleteRow && insideSecondarySchemes) {
              navigate(-1);
            }
            else {
              navigate("summary");
            }
          }}
        >
          <span className="button-save-text-claim">
            {insidePurchaseReturns ? "Proceed" : insideSecondarySchemes && handleDeleteRow ? "Save" : "Proceed"}
          </span>        
        </button>
      </GridFooterContainer>
    );
  };
  const ShelfLifeFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooter />
        <ShelfLifeCustomFooter />
      </GridFooterContainer>
    );
  };

  const GenericFooter = () => {
    if (insideOpenClaimsDetails) {
      return (
        <div style={{ width: "100%" }}>
          {footerElement && footerElement()}
          <GridFooter />
        </div>
      );
    }
    
    return (
      <GridFooterContainer>
        <div style={{width:"60%"}}>
          <GridFooter />
        </div>
        <div style={{width:"40%"}}>
          {footerElement ? footerElement() : <></>}
        </div>
      </GridFooterContainer>
    );
  };
  
  const [isBulkOrderConfirmAllowed, setIsBulkOrderConfirmAllowed] =
    useState<boolean>(isBulkOrderConfirmAllowedFromBC || false);
  const [bulkConfirmLabelConfig, setBulkConfirmLabelConfig] = useState<BulkConfirmConfigType>(defaultBulkConfirmLabels);
  const fetchRequiredConfigs = async () => {
    try {
      const orderBulkConfirmationConfig = await getConfigData(
        "order_configuration",
        "bulkOrderConfirmation"
      );
      const orderBulkConfirmationLabelConfig = await getConfigData(
        "dms_configuration",
        "bulkOrderConfirmationLabel"
      );
      setIsBulkOrderConfirmAllowed((orderBulkConfirmationConfig[0]?.configValue === true || orderBulkConfirmationConfig[0]?.configValue === "true") ?? false);
      setBulkConfirmLabelConfig(orderBulkConfirmationLabelConfig[0]?.configValue ?? defaultBulkConfirmLabels);

    } catch (e) {
      console.error("Error while fetching configs", e);
    }
  };

  const init = async () => {
    await Promise.allSettled([fetchRequiredConfigs()]);
  };

  useEffect(() => {
    init();
  }, [])

  const CreateBulkFooter = (type?: BulkType, isAnyPendingOrder?: boolean) => {
    if (manageSecondaryOrderV2 && !isBulkOrderConfirmAllowedFromBC){
       return (
      <GridFooterContainer>
        <GridFooter />
        <div className="CreateBulkInvoiceFooter_container">
          {insideManageOrder ? (
            <>
              {(numberOfSelectedInvoicedOrders ?? 0) > 0 ? (
                <div className="invoiceDownload-disclaimer">
                  <RenderSvg
                    name="invoiceDisclaimerIcon"
                    customStyles={{ color: "#B96D16", width: "10px", height: "10px" }}
                  />
                  <span>
                    <strong>{numberOfSelectedInvoicedOrders}</strong> Invoices selected
                  </span>
                </div>
              ) : (
                <div className="invoiceDownload-disclaimer">
                  <RenderSvg
                    name="disclaimerIcon"
                    customStyles={{ color: "#B96D16", width: "12px", height: "12px",  }}
                  />
                  <span>Select Invoiced Orders to download</span>
                </div>
              )}

              {printBulkInvoices && (
                <button
                  className="manageOrder-footer-invoice-download-button"
                  style={{
                    cursor:
                      printInvoicesLoading ||
                      (numberOfSelectedInvoicedOrders ?? 0) === 0
                        ? "not-allowed"
                        : "pointer",
                    backgroundColor:
                      (numberOfSelectedInvoicedOrders ?? 0) > 0 ? "#E9F9FF" : "#E2E2E2",
                    border:
                      (numberOfSelectedInvoicedOrders ?? 0) > 0
                        ? "1px solid #A8D5E5"
                        : "none",
                    color:
                      (numberOfSelectedInvoicedOrders ?? 0) > 0 ? "#227C9D" : "white",
                    height: "30px",
                    padding: "0 8px",
                  }}
                  onClick={printBulkInvoices}
                  disabled={printInvoicesLoading}
                >
                  <span style={{ fontSize: "12.5px", fontWeight: 500 }}>
                    Invoice Download
                  </span>
                  &nbsp;
                  <span style={{ width: 16 }}>
                    {printInvoicesLoading ? (
                      <CircularProgress
                        style={{
                          ...customStyle,
                          color: "#1D8DED",
                          height: 15,
                          width: 15,
                        }}
                      />
                    ) : (
                      <RenderSvg
                        name="invoiceDownloadIcon"
                        customStyles={{
                          color:
                            (numberOfSelectedInvoicedOrders ?? 0) > 0
                              ? "#227C9D"
                              : "white",
                            width: "14px"
                        }}
                      />
                    )}
                  </span>
                </button>
              )}

              {(stockAllocationAllowed && isStockAllocationButtonVisible) && <button
                className="button-save-claim"
                style={{ 
                  width: "fit-content", 
                  height: "30px", 
                  padding: "0 8px", 
                  cursor: "pointer",
                  borderRadius: "8px",
                  backgroundColor: (isAnyPendingOrder && !footerButtonLoading) ? "#00A392" : "#E2E2E2",
                  opacity: (handleStockAllocation && isAnyPendingOrder && ((checkedIdArray?.length ?? 0) > 0) && !footerButtonLoading) ? 1 : 0.6,
                  ...((!isAnyPendingOrder || footerButtonLoading) && {
                      border: "none", 
                    }),
                  pointerEvents: (handleStockAllocation && isAnyPendingOrder && ((checkedIdArray?.length ?? 0) > 0)  && !footerButtonLoading) ? "auto" : "none"
                }}
                onClick={handleStockAllocation}
                disabled={!(handleStockAllocation && isAnyPendingOrder && (checkedIdArray?.length ?? 0) > 0)}
              >
                <span className="button-save-text-claim" style={{fontWeight: 500, color: "#ffffff", fontSize: "12.5px", whiteSpace: "nowrap"}}>
                  Review & adjust stock
                </span>
              </button>}

              {(isBulkOrderConfirmAllowed || isBulkOrderConfirmAllowedFromBC || !orderConfirmationEnabled) && (
                <button
                  className="button-save-claim"
                  style={{
                    width: "fit-content",
                    height: "30px",
                    padding: "0 8px",
                    cursor: (!isAnyPendingOrder || footerButtonLoading) ? "not-allowed" : "pointer",
                    transition: "background-color 0.3s ease-in-out",
                    borderRadius: "8px",
                    whiteSpace: "nowrap",
                    backgroundColor: (isAnyPendingOrder && !footerButtonLoading) ? "#00A392" : "#E2E2E2",
                    ...((!isAnyPendingOrder || footerButtonLoading) && {
                      border: "none", 
                    }),
                    ...(type === "confirm" && {
                      backgroundColor: "#fff",
                      color: "#c64c01",
                      borderColor: "#c64c01",
                    }),
                  }}
                  onClick={handleBulkNav}
                  disabled={(footerButtonLoading || !isAnyPendingOrder)}
                >
                  <span
                    className="button-save-text-claim"
                    style={{
                      fontWeight: 500,
                      color: type === "confirm" ? "#c64c01" : "#ffffff",
                      fontSize: "12.5px",
                    }}
                  >
                    {bulkInvoiceButtonLoading ? (
                      <CircularProgress
                        style={{
                          ...customStyle,
                          color: "white",
                          height: 15,
                          width: 15,
                        }}
                      />
                    ) : orderConfirmationEnabled ? (
                      type === "confirm" ? "Go back" : "Bulk Confirm"
                    ) : (
                      "Bulk Invoice"
                    )}
                  </span>
                  <span
                    style={{
                      color: type === "confirm" ? "#c64c01" : "#ffffff",
                      fontSize: "10px",
                    }}
                  >
                    &nbsp;{type === "confirm" ? negativeCta : positiveCta}
                  </span>
                </button>
              )}
            </>
          ) : (
            <>
              {printBulkInvoices && (
                <button
                  className="ViewOrder_printInvoice"
                  style={{
                    cursor: printInvoicesLoading ? "not-allowed" : "pointer",
                    visibility: rowIdsWithCheckboxHidden?.length
                      ? "visible"
                      : "hidden",
                    marginRight: "20px",
                    alignItems: "center",
                  }}
                  onClick={printBulkInvoices}
                  disabled={printInvoicesLoading}
                >
                  <span>Print Invoices</span>&nbsp;
                  <span style={{ width: 20 }}>
                    {printInvoicesLoading ? (
                      <CircularProgress
                        style={{
                          ...customStyle,
                          color: "#1D8DED",
                          height: 15,
                          width: 15,
                        }}
                      />
                    ) : (
                      <PdfIcon />
                    )}
                  </span>
                </button>
              )}


{type !== undefined && rows?.length > 0 && (
  <button
    className="button-save-claim"
    style={{
      width: "fit-content",
      height: "35px",
      padding: "0 10px",
      cursor: footerButtonLoading ? "not-allowed" : "pointer",
      transition: "background-color 0.3s ease-in-out",
      borderRadius: "8px",
      visibility: rows?.length ? "visible" : "hidden",
      backgroundColor: "#00A392", 
      color: "#ffffff",     
      border: "none",
      ...(type === "confirm" && {
        backgroundColor: "#fff",
        color: "#c64c01",
        borderColor: "#c64c01",
      }),
    }}
    onClick={handleClickBulk}
    disabled={footerButtonLoading}
  >
    <span
      className="button-save-text-claim"
      style={{
        fontWeight: 500,
        fontSize: "14.5px",
        display: "flex",
        alignItems: "center",
      }}
    >
      {footerButtonLoading ? (
        <CircularProgress
          style={{
            height: 15,
            width: 15,
            color: type === "confirm" ? "#c64c01" : "white",
            marginRight: 5,
          }}
        />
      ) : type === "invoice" ? (
        "Start Invoicing"
      ) : (
        "Confirm Orders"
      )}
    </span>
    <span style={{ color: type === "confirm" ? "#c64c01" : "#ffffff", fontSize: "10px" }}>
      &nbsp;{positiveCta}
    </span>
  </button>
)}


           
            </>
          )}
        </div>

       
      </GridFooterContainer>
    );
    }
    return (
      <GridFooterContainer>
        <GridFooter />
        <div className="CreateBulkInvoiceFooter_container">
        { (isAnyPendingOrder && isBulkOrderConfirmAllowed) && (
          <button
            className="button-save-claim"
            style={{
                width: "fit-content",
                height: "35px",
                padding: "0 10px",
                cursor: footerButtonLoading ? "not-allowed" : "pointer",
                ...(type === "confirm" && {
                  backgroundColor: "#fff",
                  color: "#c64c01",
                  borderColor: "#c64c01",
                }),
              }}
            onClick={handleBulkNav && handleBulkNav}
            disabled={footerButtonLoading}
          >
          <span
            className="button-save-text-claim"
              style={{
              fontWeight: 500,
              color: type === "confirm" ? "#c64c01" : "#ffffff",
              fontSize: "14.5px",
            }}
          >
            {orderConfirmationEnabled ? (type === "confirm" ? "Go back" : bulkConfirmLabelConfig.preFooterLabel) : "Bulk Invoice"}
          </span>
          {<span style={{color: type === "confirm" ? "#c64c01" : "#ffffff", fontSize:"10px"}}>&nbsp;{type === "confirm" ? negativeCta : positiveCta}</span>}

          </button>
        ) }
        { printBulkInvoices && <button 
            className="ViewOrder_printInvoice"
            style={{ cursor: printInvoicesLoading ? "not-allowed" : "pointer", 
                      visibility: rowIdsWithCheckboxHidden?.length ? "visible" : "hidden", marginRight: "20px", alignItems: "center" }}
            onClick={printBulkInvoices}
            disabled={printInvoicesLoading}
          >
            <span>Print Invoices</span>&nbsp;
            <span style={{width: 20}}>
            {printInvoicesLoading ? (
               <CircularProgress
               style={{
                 ...customStyle,
                 color: "#1D8DED",
                 height: 15,
                 width: 15,
               }}
             />
            ) : (
              <PdfIcon />
            )}
            </span>

          </button> }
          { ((type !== undefined) && rows?.length > 0) && (
            <button
              className="button-save-claim"
              style={{ width: "fit-content", height: "35px", padding: "0 10px", cursor: footerButtonLoading ? "not-allowed" : "pointer", visibility : rows?.length ? "visible" : "hidden"}}
              onClick={handleClickBulk && handleClickBulk}
              disabled={footerButtonLoading}
            >
              <span className="button-save-text-claim" style={{fontWeight: 500}}>{type === "invoice" ? "Start Invoicing" : bulkConfirmLabelConfig.postFooterLabel}</span>
              {<span style={{color: "#ffffff", fontSize:"10px"}}>&nbsp;{positiveCta}</span>}
            </button>
          )}
        </div>
       
      </GridFooterContainer>
    );
  }

  const outletApprovalFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooter/>
        <div className="outlet_approval_footer_legend">
          <StarsRoundedIcon
            sx={{
              color: "#FDB42C",
              background: "#FFE8BF",
              height: "15px",
              width: "15px",
              borderRadius: "50%",
            }}
          />
          <span style={{fontSize:"14px"}}>Key Accounts</span>
        </div>
      </GridFooterContainer>

    )
  }

  const ReviewStocksFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooter/>
        <div className="CreateBulkInvoiceFooter_container">
          <button
            className="button-save-claim"
            style={{
               width: "fit-content",
              height: "35px",
              padding: "0 10px",
              cursor: (footerButtonLoading || isAnyManualAndPending) ? "not-allowed" : "pointer",
              transition: "background-color 0.3s ease-in-out",
              borderRadius: "8px",
              backgroundColor: (footerButtonLoading || isAnyManualAndPending) ? "#bfbfbf" : "#00A392", 
              color: "#ffffff",     
              border: "none",
              pointerEvents: (footerButtonLoading || isAnyManualAndPending) ? "none" : "auto",
              opacity: (footerButtonLoading || isAnyManualAndPending) ? 0.7 : 1, // subtle dimming
            }}
            onClick={handleBulkInvoiceModalClick}
            disabled={footerButtonLoading || isAnyManualAndPending}
          >
            <span
              className="button-save-text-claim"
              style={{
                fontWeight: 500,
                fontSize: "14.5px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {footerButtonLoading ? (
                <CircularProgress
                  style={{
                    height: 15,
                    width: 15,
                    color: "white",
                    marginRight: 5,
                  }}
                />
              ) : "Bulk Invoice"
              }
            </span>
          </button>
        </div>
      </GridFooterContainer>
    )
  }
  const BulkInvoiceModalFooter = () => {
    return (
      <GridFooterContainer>
        <GridFooter/>
        <div className="CreateBulkInvoiceFooter_container">
          <button
            className="button-save-claim"
            style={{
              width: "fit-content",
              height: "35px",
              padding: "0 10px",
              cursor: (footerButtonLoading || !isAnyPendingOrder) ? "not-allowed" : "pointer",
              transition: "background-color 0.3s ease-in-out, opacity 0.3s ease-in-out",
              borderRadius: "8px",
              visibility: rows?.length ? "visible" : "hidden",
              backgroundColor: (footerButtonLoading || !isAnyPendingOrder) ? "#B9B9B9" : "#00A392", 
              color: "#ffffff",     
              border: "none",
              opacity: (footerButtonLoading || !isAnyPendingOrder) ? 0.7 : 1,
            }}
            onClick={handleBulkInvoicingForManualStockAllocation}
            disabled={footerButtonLoading || !isAnyPendingOrder}
          >
            <span
              className="button-save-text-claim"
              style={{
                fontWeight: 500,
                fontSize: "14.5px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {footerButtonLoading ? (
                <CircularProgress
                  style={{
                    height: 15,
                    width: 15,
                    color: "white",
                    marginRight: 5,
                  }}
                />
              ) : "Bulk Invoice"
              }
            </span>
          </button>
        </div>
      </GridFooterContainer>
    )
  }

  return (
    <div className="CustomDataGrid">      
      <GenericPopUp
        message={genericMessage}
        type={popupType}
        openGenericModal={openGenericModal}
        setOpenGenericModal={setOpenGenericModal}
      />
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>

      {renderGrid && (
        <DataGrid
          className="data-grid ignore-scroll-detection"
          disableRowSelectionOnClick={true}
          {...(isCheckboxSelection && {
            checkboxSelection: true,
            isRowSelectable: (params)=>{
              if (rowIdsWithCheckboxHidden?.includes(params.id)) {
                return false; 
              }
              if(selectableRowIds){
                return selectableRowIds.includes(params.id);
              }else{
                return true;
              }
            },
            onRowSelectionModelChange: (ids) => {
              if (setCheckedIdArray) {
                const filteredIds = ids.filter((id) => !rowIdsWithCheckboxHidden?.includes(id));
                setCheckedIdArray(filteredIds);
              }
            },
            rowSelectionModel: checkedIdArray,
          })}
          getRowClassName={(params) => {
            const classes = [];
            const isLastRow = rows[rows.length - 1]?.id === params.id;
            const hasZeroClaimAmount = params.row.totalClaimAmount === 0;

            if (rowIdsWithCheckboxHidden?.includes(params.row.id)) {
              classes.push("disable-checkbox");
            }
            if (insideSaleReturns && hasZeroClaimAmount) {
              classes.push("disable-row");
            }
            if (params.id === selectedRowId) {
              classes.push("selected-row");
            }

            if (isLastRow) {
              classes.push("last-row");
            }

            return classes.join(" ");
          }}     
          onRowClick={(params) => {
            if (onRowClick && insideOpenClaimsDetails) {
              onRowClick(params);
            } else if (onRowClick && insideManualStockAllocation){
              if (params?.row?.allocationMethod.toUpperCase() === "MANUAL") onRowClick(params?.row)
            } 
            else if (enableRowClickRedirect && insidePurchaseReturns && handleClickEditPurchaseReturnsRow){
              handleClickEditPurchaseReturnsRow(params.row)
            } else if (enableRowClickRedirect && !redirectUrl && insideSecondarySchemes && setSelectedSecondarySchemesRow){
                dispatch(setSelectedSecondarySchemesRow(params.row));
                navigate("invoiceDetails");
            }
            
            else if (enableRowClickRedirect && redirectUrl && !disabledRowIds.includes(params.id)) {
              // Prevent row click if clicking on action columns (edit/view buttons)
              const target = (params as any).event?.target as HTMLElement;
              if (target?.closest('.manage_order_table_edit_button') || 
                  target?.closest('button') || 
                  target?.closest('input') ||
                  target?.closest('select')) {
                return;
              }
              
              // Check if there's an eyeIcon column in the metadata
              const hasEyeIconColumn = columnsMetaData.columns.some(
                (col: any) => col.mappedValue?.toLowerCase() === "eyeicon"
              );
              
              // Handle row click redirection for order/invoice screens
              if (onEditClick) {
                onEditClick(params.row);
                return;
              }
              if (redirectUrlFunction) {
                redirectUrlFunction(redirectUrl, params.row);
                return;
              }
              
              // If there's an eyeIcon column, use the same logic as the eye icon button
              if (hasEyeIconColumn && redirectUrl) {
                navigate(`/dashboard${redirectUrl}`, {
                  state: params.row,
                });
                return;
              }
              
              const url = getRowClickRedirectUrl(params.row);
              navigate(url, {
                state: {
                  row: params.row,
                  ...locationState
                },
              });
            } else if (enableRowClickRedirect && !redirectUrl && !disabledRowIds.includes(params.id) && onEditClick) {
              // If no redirectUrl is provided, but onEditClick is defined, call it
              onEditClick(params.row); }
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          sx={{
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
              outline: "none !important",
            },
            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-virtualScroller": {
              scrollbarWidth: (ConfigDictionary?.showTableScrollbar ?? false) ? "thin" : "none",
              overflowY: allowTableScroll ? "auto" : "hidden",
            },
            ...((ConfigDictionary?.showTableScrollbar ?? false) && {
              "& .MuiDataGrid-virtualScrollerContent": {
                minWidth: "100% !important",      // always at least full container width
                width: "100% !important"
              },
            }),
            "& .MuiDataGrid-columnSeparator": {
              display: "none",
            },
            "& .MuiDataGrid-cell": {
              borderColor: "#eaeaea",
            },
            "& .MuiTablePagination-root p": {
              margin: 0,
            },
            "& .Mui-selected": {
              backgroundColor: "transparent !important",
            },
            "& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root": {
              color: "#00C6B1",
            },
            "& .MuiDataGrid-footerContainer": {
              paddingRight: "14px",
              borderTop: "1px solid rgba(224, 224, 224, 1)",
              borderBottom: "1px solid rgba(224, 224, 224, 1)"
            },
            "& .MuiDataGrid-footerContainer .MuiDataGrid-footerContainer": {
              border: "0",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "transparent !important",
            },
            "& .MuiDataGrid-row.disable-checkbox .MuiDataGrid-cellCheckbox": {
              pointerEvents: "none",
              opacity: 0.5,
            },
            "& .MuiDataGrid-row.selected-row": {
              backgroundColor: "#F2FDFE !important",
              border: "1px solid #21C6B1 !important",
            },
            "& .MuiDataGrid-row.last-row .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .MuiDataGrid-row.disable-row": {
              backgroundColor: "#fff9f9 !important",
              "&:hover": {
                backgroundColor: "#fff9f9 !important",
              },
            },
            ...(enableRowClickRedirect && {
              "& .MuiDataGrid-row": {
                cursor: "pointer",
                transition: "background-color 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#f6fffd !important",
                },
              },
              "& .MuiDataGrid-cell": {
                cursor: "pointer",
              },
            }),
            borderColor: "#eaeaea",
            fontSize: "13px",
            color: "#909090",
            fontWeight: 500,
          }}
          rows={rows}
          columns={columns}
          hideFooter={isFooterVisible == undefined ? false : !isFooterVisible}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            baseCheckbox: (props) => {
              // Check if the row is faded
              const isFaded = fadedRowIds?.includes(props.id);
              return (
                <Checkbox
                  {...props}
                  sx={{
                    "& .MuiSvgIcon-root": {
                        fontSize: 21
                      },
                    ...(isFaded && {
                      opacity: 0.4,
                      '& .MuiSvgIcon-root': {
                        opacity: 0.4,
                      },
                    }),
                  }}
                />
              );
            },
            ...((handleClickBulk || handleBulkNav)
              ? { footer: () => CreateBulkFooter(bulkType, isAnyPendingOrder)}
              : (handleBulkInvoiceModalClick) ?
              { footer: () => ReviewStocksFooter()}
              :
              (insideBulkInvoiceModal) ? 
              {footer: () => BulkInvoiceModalFooter()}
              : showShelfLifeFooter
              ? { footer: ShelfLifeFooter }
              : insideSaleReturns ||
              insidePurchaseReturns ||
              insideSecondarySchemes ||
              handleDeleteRow 
              ? {footer: CustomFooter}
              : insideOutletApproval
              ? {footer: outletApprovalFooter}
              :footerElement
              ? {footer:GenericFooter}
              : {}),
          }}
          columnVisibilityModel={columnVisibility}
          initialState={{
            sorting: {
              sortModel: [{ field: "adjustmentDate", sort: "desc" }],
            },
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          components={
            showShelfLifeFooter
              ? {
                  Footer: ShelfLifeFooter,
                }
              : insideSaleReturns ||
                insidePurchaseReturns ||
                insideSecondarySchemes ||
                handleDeleteRow
              ? {
                  Footer: CustomFooter,
                }
              : {}
          }
        />
      )}

      {/* Outlet Wise Allocation Modal */}
      
    </div>
  );
};

export default CustomDataGrid;
